/**
 * A page's title, and what belongs to the page rather than to a card.
 *
 * **This emits the application's only `<h1>`.** There was none: 102 `<h2>` elements and nothing
 * above them, so every route's heading tree started at level two — which is the outline a screen
 * reader walks to find out where it is. Adopting this therefore shifts a page's existing `<h2>`
 * elements down a level, and that is the correction rather than a side effect of it.
 *
 * `actions` is a slot of its own rather than more children, because what went wrong with
 * `.card__head` is exactly that it holds either: seventy-eight of them, twenty-five a title with
 * actions and forty-three actions alone with no title at all, all sharing one
 * `justify-content: space-between` that scatters a wrapped row. A header with a title slot and an
 * actions slot cannot be used as a footer; `SubmitBar` is the footer.
 *
 * ## A title is words or a reference, and the compiler decides which (D3's remainder)
 *
 * The twenty-seven screens are titled from the route table, in words. The five record routes are
 * titled by the record — and for two of them the record's name *is* an Order reference, which must
 * keep its direction: §8's rule is that a reference is a single token read left to right, and
 * letting the bidi algorithm reorder its parts around Arabic is how a reference read over the phone
 * stops matching the one on the parcel. That is register row 32 at heading size.
 *
 * So the two are separate props and the union refuses both at once and neither. The gain is not
 * tidiness:
 *
 * - a page cannot put a sentence in the reference slot and force Arabic prose left to right,
 *   because `.mono` is the selector `forced-ltr.testkit.ts` reads out of the stylesheet, so the
 *   watcher fails any test that renders right-to-left text through it;
 * - and it cannot put a reference in the words slot and lose the isolation, because the only way
 *   to get `.mono` here is to say the value is a reference.
 *
 * Which is the same argument `Amount` makes by having no numeric prop: the decision moves from
 * forty call sites into one component.
 *
 * `status` is third rather than folded into `actions`, because a record's state is not an act. The
 * two record pages that carry one showed it at the far edge of a `space-between` card head; it
 * reads better beside the title, and `actions` keeps the far edge.
 */
import type { ReactNode } from 'react';

type Titled =
  /** Words: a screen's own name, from the route table or the page's copy. */
  | { readonly title: string }
  /** A token read left to right: an Order reference, a barcode, a public id. */
  | { readonly reference: string };

export function PageHeader(
  props: Titled & { readonly status?: ReactNode; readonly actions?: ReactNode },
): React.JSX.Element {
  const isReference = 'reference' in props;
  return (
    <header className="page-head">
      <h1 className={isReference ? 'page-head__title mono' : 'page-head__title'}>
        {'reference' in props ? props.reference : props.title}
      </h1>
      {props.status !== undefined && <div className="page-head__status">{props.status}</div>}
      {props.actions !== undefined && <div className="page-head__actions">{props.actions}</div>}
    </header>
  );
}
